import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import NotFoundPage from '@/pages/NotFoundPage';
import { getSaftCountryBySlug } from '@/data/saftCountries';
import { SaftCountryContent } from '@/pages/SaftCountryPage';
// Print-friendly version of a SAF-T country page: same content, without site header, footer, chat or banners.
// Used to produce the PDF guide. Not meant to be found in search engines.
const SaftGuidePrintPage = () => {
  const { country } = useParams();
  const [params] = useSearchParams();
  const c = getSaftCountryBySlug(country);
  useEffect(() => {
    if (!c) return undefined;
    // Keep this page out of search results.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    // Hide everything on the page that is not the guide (header, footer, chat widget, cookie banner).
    // Some of these appear a moment after the page loads, so the hiding is repeated whenever the page changes.
    const hidden = new Map();
    const hideOthers = () => {
      const root = document.querySelector('[data-print-root]');
      let el = root;
      while (el && el !== document.body && el.parentElement) {
        Array.from(el.parentElement.children).forEach((sib) => {
          if (sib !== el && !['SCRIPT', 'STYLE', 'LINK', 'META', 'NOSCRIPT'].includes(sib.tagName) && !hidden.has(sib)) {
            hidden.set(sib, sib.style.display);
            sib.style.setProperty('display', 'none', 'important');
          }
        });
        el = el.parentElement;
      }
    };
    hideOthers();
    const observer = new MutationObserver(hideOthers);
    observer.observe(document.body, { childList: true, subtree: true });
    // When a visitor prints this page themselves, remove the browser's own header and footer lines (date, web address).
    const manual = params.get('autoprint') === '1';
    const style = document.createElement('style');
    style.textContent = `
      ${manual ? '@page { size: A4; margin: 0; } [data-print-root] { padding: 14mm 12mm; }' : '@page { size: A4; margin: 14mm 12mm; }'}
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      .saft-print section, .saft-print li, .saft-print .rounded-2xl, .saft-print .rounded-xl { break-inside: avoid; }
      .saft-print h1, .saft-print h2 { break-after: avoid; }
      .saft-print section { padding-top: 0 !important; padding-bottom: 0 !important; }
      .saft-print section > div[class*="py-14"] { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; }
      .saft-print section[class*="py-14"] { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; }
      .saft-print section[class*="pt-10"] { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; }
      .saft-print section[class*="pb-20"] { padding-bottom: 1rem !important; }
      .saft-print h1 { font-size: 2.25rem !important; }
    `;
    document.head.appendChild(style);
    let timer;
    if (params.get('autoprint') === '1') {
      timer = window.setTimeout(() => window.print(), 1200);
    }
    return () => {
      observer.disconnect();
      meta.remove();
      style.remove();
      hidden.forEach((display, node) => {
        node.style.display = display;
      });
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c && c.slug]);
  if (!c) return <NotFoundPage />;
  return (
    <div data-print-root data-testid={`saft-guide-print-${c.slug}`} className="bg-white min-h-screen">
      <SaftCountryContent c={c} print />
    </div>
  );
};
export default SaftGuidePrintPage;
